import React, { useEffect } from "react";
import { Coffee, Loader2 } from "lucide-react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { fetchSkipMealBreakStatus, skipMealBreak } from "../redux/features/common";
import { RootState, useAppDispatch } from "../redux/store";
import { SkipMealBreakStatusResponseData } from "../types";
import { DateTimeUtil } from "../utils";
import { formatMealBreakTime, getMealBreakToastMessage } from "../utils/mealBreakDashboard";

type SkipMealBreakTileProps = {
    className?: string;
    refreshKey?: string | number;
};

export const FIVE_MINUTES_MS = 5 * 60 * 1000;

export const shouldFetchSkipMealBreakStatus = (
    _skipMealBreakStatusData: SkipMealBreakStatusResponseData | null,
    skipMealBreakStatusLastFetched: number | null,
    now: number = Date.now()
): boolean => {
    return !skipMealBreakStatusLastFetched || now - skipMealBreakStatusLastFetched > FIVE_MINUTES_MS;
};

export const isSkipMealBreakRequested = (
    skipMealBreakStatusData: SkipMealBreakStatusResponseData | null
): boolean => {
    return skipMealBreakStatusData?.skip_meal_break_status === "REQUESTED";
};

export const isSkipMealBreakAlreadyRequested = (
    skipMealBreakStatusData: SkipMealBreakStatusResponseData | null
): boolean => {
    return skipMealBreakStatusData?.skip_meal_break_status === "ALREADY_REQUESTED";
};

export const isSkipMealBreakAvailable = (
    skipMealBreakStatusData: SkipMealBreakStatusResponseData | null
): boolean => {
    return skipMealBreakStatusData?.skip_meal_break_status === "AVAILABLE";
};

export const isSkipMealBreakNotEligible = (
    skipMealBreakStatusData: SkipMealBreakStatusResponseData | null
): boolean => {
    return skipMealBreakStatusData?.skip_meal_break_status === "NOT_ELIGIBLE";
};

export const getSkipMealBreakRequestedLabel = (
    skipMealBreakStatusData: SkipMealBreakStatusResponseData | null
): string => {
    const requestedAt = getSkipMealBreakRequestedTimeLabel(skipMealBreakStatusData);
    return requestedAt ? `Requested at ${requestedAt}` : "Skipped for Today";
};

export const getSkipMealBreakRequestedTimeLabel = (
    skipMealBreakStatusData: SkipMealBreakStatusResponseData | null
): string => {
    return formatMealBreakTime(skipMealBreakStatusData?.request_datetime);
};

export const getSkipMealBreakLoadErrorLabel = (): string => {
    return "Couldn't load skip meal break status.";
};

export const getSkipMealBreakAlreadyRequestedLabel = (): string => {
    return "Skip meal request already raised.";
};

export const getSkipMealBreakStateLabel = (
    skipMealBreakStatusData: SkipMealBreakStatusResponseData | null
): string => {
    if (isSkipMealBreakAlreadyRequested(skipMealBreakStatusData)) {
        return getSkipMealBreakAlreadyRequestedLabel();
    }

    if (isSkipMealBreakRequested(skipMealBreakStatusData)) {
        return getSkipMealBreakRequestedLabel(skipMealBreakStatusData);
    }

    return "";
};

export const isSkipMealBreakNoteValid = (note: string): boolean => {
    return note.trim().length > 0;
};

export const getSkipMealBreakShiftStartedLabel = (
    skipMealBreakStatusData: SkipMealBreakStatusResponseData | null
): string => {
    return formatMealBreakTime(skipMealBreakStatusData?.clockin_datetime);
};

export const getSkipMealBreakApprovalStatusLabel = (
    skipMealBreakStatusData: SkipMealBreakStatusResponseData | null
): string => {
    const approvalStatus = skipMealBreakStatusData?.approval_status?.trim();

    if (!approvalStatus) {
        return "";
    }

    return approvalStatus
        .toLowerCase()
        .split(/[_\s]+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

export const getSkipMealBreakApprovalStatusClasses = (
    skipMealBreakStatusData: SkipMealBreakStatusResponseData | null
): string => {
    const approvalStatus = skipMealBreakStatusData?.approval_status?.trim().toUpperCase();

    if (approvalStatus === "APPROVED" || approvalStatus === "APPROVE") {
        return "text-green-700";
    }

    if (approvalStatus === "REJECTED" || approvalStatus === "REJECT") {
        return "text-red-700";
    }

    if (approvalStatus === "PENDING") {
        return "text-amber-700";
    }

    return "text-blue-700";
};

const SkipMealBreakTile: React.FC<SkipMealBreakTileProps> = ({ className = "", refreshKey }) => {
    const dispatch = useAppDispatch();
    const {
        skipMealBreakStatusData,
        isLoadingSkipMealBreakStatus,
        skipMealBreakStatusError,
    } = useSelector((state: RootState) => state.common);
    const [isSkipping, setIsSkipping] = React.useState(false);
    const [skipMealBreakNote, setSkipMealBreakNote] = React.useState("");
    const [skipMealBreakNoteError, setSkipMealBreakNoteError] = React.useState<string | null>(null);
    const previousRefreshKeyRef = React.useRef<string | number | undefined>(refreshKey);

    const loadSkipMealBreakStatus = React.useCallback(() => {
        dispatch(
            fetchSkipMealBreakStatus({
                datetime: DateTimeUtil.formatDateToMySQL(new Date()),
            })
        );
    }, [dispatch]);

    useEffect(() => {
        loadSkipMealBreakStatus();
    }, [loadSkipMealBreakStatus]);

    useEffect(() => {
        if (refreshKey === undefined) {
            return;
        }

        if (previousRefreshKeyRef.current === undefined) {
            previousRefreshKeyRef.current = refreshKey;
            return;
        }

        if (previousRefreshKeyRef.current !== refreshKey) {
            previousRefreshKeyRef.current = refreshKey;
            loadSkipMealBreakStatus();
        }
    }, [loadSkipMealBreakStatus, refreshKey]);

    const isRequested = isSkipMealBreakRequested(skipMealBreakStatusData);
    const isAlreadyRequested = isSkipMealBreakAlreadyRequested(skipMealBreakStatusData);
    const isAvailable = isSkipMealBreakAvailable(skipMealBreakStatusData);
    const isNotEligible = isSkipMealBreakNotEligible(skipMealBreakStatusData);
    const shiftStartedAt = getSkipMealBreakShiftStartedLabel(skipMealBreakStatusData);
    const approvalStatusLabel = getSkipMealBreakApprovalStatusLabel(skipMealBreakStatusData);
    const showLoader = isLoadingSkipMealBreakStatus;

    const handleSkipMealBreak = async () => {
        if (!isAvailable) {
            return;
        }

        if (!isSkipMealBreakNoteValid(skipMealBreakNote)) {
            setSkipMealBreakNoteError("A note is required.");
            return;
        }

        const workflowCode = skipMealBreakStatusData?.workflow_code;
        const entryId = Number(skipMealBreakStatusData?.schedules_id);

        if (!workflowCode) {
            toast.error("Meal break workflow code is missing");
            return;
        }

        if (!entryId || Number.isNaN(entryId)) {
            toast.error("Meal break entry id is missing");
            return;
        }

        setIsSkipping(true);
        try {
            const result = await dispatch(
                skipMealBreak({
                    workflow_code: workflowCode,
                    entry_id: entryId,
                    current_datetime: DateTimeUtil.formatDateToMySQL(new Date()),
                    notes: skipMealBreakNote.trim(),
                })
            );

            if (skipMealBreak.fulfilled.match(result)) {
                const successMessage = getMealBreakToastMessage(result.payload);
                if (successMessage) {
                    toast.success(successMessage);
                }
                setSkipMealBreakNote("");
                setSkipMealBreakNoteError(null);
                loadSkipMealBreakStatus();
            } else {
                const errorMessage = getMealBreakToastMessage(result.payload);
                if (errorMessage) {
                    toast.error(errorMessage);
                }
            }
        } finally {
            setIsSkipping(false);
        }
    };

    const getStatusContent = () => {
        if (skipMealBreakStatusError) {
            return (
                <div className="space-y-2">
                    <p className="text-sm font-medium text-red-600">{getSkipMealBreakLoadErrorLabel()}</p>
                    <button
                        type="button"
                        onClick={loadSkipMealBreakStatus}
                        disabled={isLoadingSkipMealBreakStatus}
                        className="text-sm font-medium text-blue-700 underline underline-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        Tap to retry.
                    </button>
                </div>
            );
        }

        if (isRequested || isAlreadyRequested || approvalStatusLabel) {
            const requestedAt = getSkipMealBreakRequestedTimeLabel(skipMealBreakStatusData);
            const requestLabel = getSkipMealBreakStateLabel(skipMealBreakStatusData);
            const shouldShowRequestLabel = Boolean(requestLabel) && (!requestedAt || isAlreadyRequested);

            return (
                <div className="space-y-3">
                    {shouldShowRequestLabel ? (
                        <p className="text-sm text-gray-500">{requestLabel}</p>
                    ) : null}

                    {requestedAt ? (
                        <div>
                            <span className="text-gray-500">Requested at</span>{" "}
                            <span className="font-medium text-gray-700">{requestedAt}</span>
                        </div>
                    ) : null}

                    {approvalStatusLabel && (
                        <p className={`text-sm font-medium ${getSkipMealBreakApprovalStatusClasses(skipMealBreakStatusData)}`}>
                            Approval Status: {approvalStatusLabel}
                        </p>
                    )}
                </div>
            );
        }

        if (isNotEligible || !isAvailable) {
            return (
                <p className="text-sm text-gray-500">Available after you clock in.</p>
            );
        }

        return (
            <>
                <p>Skipping your lunch break means you will not take a break for this shift.</p>

                <div className="max-w-full space-y-2 md:max-w-sm">
                    <label htmlFor="skip-meal-break-note" className="block text-sm font-medium text-slate-700">
                        Note <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        id="skip-meal-break-note"
                        value={skipMealBreakNote}
                        onChange={(event) => {
                            setSkipMealBreakNote(event.target.value);
                            if (skipMealBreakNoteError && isSkipMealBreakNoteValid(event.target.value)) {
                                setSkipMealBreakNoteError(null);
                            }
                        }}
                        rows={3}
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        placeholder="Tell your manager why you need to skip your lunch break."
                    />
                    {skipMealBreakNoteError ? (
                        <p className="text-sm font-medium text-red-600">{skipMealBreakNoteError}</p>
                    ) : null}
                </div>

                <button
                    type="button"
                    onClick={handleSkipMealBreak}
                    disabled={isSkipping}
                    className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 transition-colors hover:border-blue-300 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {isSkipping ? "Submitting..." : "Skip Lunch Break"}
                </button>
            </>
        );
    };

    return (
        <div className={`h-full rounded-xl border border-gray-300 bg-white ${className}`}>
            <div className="p-6 h-full">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg text-blue-600 leading-tight hover:text-blue-600 transition-colors">
                        Skip Lunch Break
                    </h2>
                    <Coffee className="w-5 h-5 flex-shrink-0 text-gray-400" />
                </div>

                <div className="flex-1 space-y-3 text-sm leading-relaxed text-gray-600">
                    {shiftStartedAt && !showLoader && (
                        <div>
                            <span className="text-gray-500">Shift started at</span>{" "}
                            <span className="font-medium text-gray-700">{shiftStartedAt}</span>
                        </div>
                    )}

                    {showLoader ? (
                        <div className="flex justify-center py-4">
                            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                        </div>
                    ) : (
                        getStatusContent()
                    )}
                </div>
            </div>
        </div>
    );
};

export default SkipMealBreakTile;
